import React, { useState } from 'react'
import styles from './Details.scss'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/SideNavLayout/TwoCol'
import { SectionBox } from 'common/styleguide/SectionBox'
import { CTA } from 'common/styleguide/CTA'
import { ImageUpload, reduceBlobURL } from 'common/styleguide/ImageUpload'
import { CircleSpinner } from 'common/spinners'
import { useFormatMessage, commonNouns, commonVerbs, commonCTAs, privacyLevels, commonMessages } from 'lib/localization'
import { c } from 'lib/textUtils'
import { Goal } from 'data/records'
import plusIcon from 'assets/images/goals/plusIcon.svg'
import { Map } from 'immutable'
import { defineMessages } from 'react-intl'

const clubVisibilityOptions = fm => [
  { value: 0, title: fm(privacyLevels.public) },
  { value: 1, title: fm(privacyLevels.rmOnly) },
  { value: 2, title: fm(privacyLevels.mOnly) }
]

const messages = defineMessages({
  coverPhoto: {
    id: 'details.coverPhoto',
    defaultMessage: 'Cover Photo'
  },
  filesize: {
    id: 'details.filesize',
    defaultMessage: 'Files should be no larger than 10MB. If no images are added, the logo and cover images from your organization account will be used.'
  }
})

export const Details = ({ goal, updateGoal }) => {
  const fm = useFormatMessage()
  const [savePending, setSavePending] = useState(false)
  const [pendingEdits, setPendingEdits] = useState(Map())
  const onInputChange = e => setPendingEdit(e.target.name, e.target.value)
  const setPendingEdit = (key, value) => setPendingEdits(pendingEdits.merge({ [key]: value }))
  const onLogoDrop = files => {
    if (files && files[0]) {
      setPendingEdit('icon', files[0])
    }
  }
  const onCoverDrop = files => {
    if (files && files[0]) {
      setPendingEdit('cover', files[0])
    }
  }

  const values = goal.merge(pendingEdits)
  const onSubmit = () => {
    if (savePending) { return }
    setSavePending(true)

    const params = Goal.serialize(values, ['name', 'description', 'visibility', 'cover', 'icon'])
    updateGoal(goal.id, params)
      .then(() => setPendingEdits(Map()))
      .finally(() => {
        window.location = `/challenges/${goal.get('id')}/overview`
        setSavePending(false)
      })
  }

  return (
    <ColWrap>
      <MainCol>
        <SectionBox title={fm(commonNouns.details)}>
          <form onSubmit={onSubmit}>
            <div>
              <label>{c(fm(commonNouns.title))}</label>
              <input type='text' value={values.name} name='name' onChange={e => setPendingEdit('name', e.target.value.slice(0, 255))} maxlength='255' />
            </div>
            <div>
              <label>{c(fm(commonNouns.description))}</label>
              <textarea rows='3' value={values.description} name='description' onChange={onInputChange} />
            </div>
            <div>
              <label>{fm(commonNouns.logo)} (150px x 150px)</label>
              <ImageUpload
                onDrop={onLogoDrop}
                className={styles.logoUpload}
              >
                {values.icon
                  ? <img height={80} src={reduceBlobURL(values.icon, goal.get('updatedAt'))} />
                  : <img className={styles.plusIcon} height='80' width='80' src={plusIcon} />}
              </ImageUpload>
            </div>
            <div>
              <label>{fm(messages.coverPhoto)} (1280px x 740px)</label>
              <ImageUpload
                onDrop={onCoverDrop}
              >
                {values.cover
                  ? <img height={80} src={reduceBlobURL(values.cover, goal.get('updatedAt'))} />
                  : <img className={styles.plusIcon} height='80' width='80' src={plusIcon} />}
              </ImageUpload>
              <p className={styles.disclaimer}>{fm(messages.filesize)}</p>
            </div>
            <div className={styles.visibilityContainer}>
              <label>{fm(commonNouns.visibility)}</label>
              <select className={styles.visibilitySelect} name='visibility' value={values.visibility || 0} onChange={onInputChange}>
                {clubVisibilityOptions(fm).map(o => <option key={o.value} value={o.value}>{o.title}</option>)}
              </select>
            </div>
          </form>
        </SectionBox>
      </MainCol>
      <SecondaryCol>
        <SectionBox title={fm(commonVerbs.save)}>
          <CTA
            onClick={onSubmit}
            disabled={savePending || pendingEdits.size === 0}
            margins={false}>
            {savePending && <>
              <CircleSpinner size={14} />
              <span style={{ marginLeft: '10px' }}>{fm(commonMessages.saving)}</span>
              </>}
            {!savePending && <>
                {pendingEdits.size === 0
                  ? fm(commonMessages.changesSaved)
                  : fm(commonVerbs.save)}
              </>}
          </CTA>
          <CTA link to={`/challenges/${goal.id}/overview`} margins={false} style={{ paddingBottom: '0px', marginTop: '8px' }}>
            {fm(commonCTAs.cancel)}
          </CTA>
        </SectionBox>
      </SecondaryCol>
    </ColWrap>
  )
}
